/**
 * The runtime fighter record. All gameplay-authoritative fighter data lives here and is
 * written ONLY from `fixedUpdate` (ARCHITECTURE.md §1.2).
 *
 * Every field is initialised in `createFighterState` so the object shape never changes at
 * runtime — no hidden-class transitions, no megamorphic property access, no allocation.
 */

import { GROUND_Y, SPAWN_X, F60_TO_TICKS } from '../core/constants.js';
import { S, isAirborneState, isCrouchingState, isGuardingState, isAttackingState,
  isHitstunState, isDownState, isThrowableState, isCounterHitState, isPunishableState,
  isJuggleableState, isLockedState, isActionableState } from './states.js';
import { getStats } from './roster.js';

/** Max simultaneous hitboxes tracked per move for once-per-move hit flags. */
export const MAX_HITBOXES = 8;
/** Depth of the per-fighter action log used by training mode and the AI's habit tracker. */
export const ACTION_LOG_SIZE = 32;

/**
 * @param {number} index 0 = P1, 1 = P2
 * @param {string} fighterId 'kestrel' | 'bulwark'
 * @returns {object} FighterState
 */
export function createFighterState(index, fighterId) {
  const stats = getStats(fighterId);
  const f = {
    // ---- identity ----
    index,
    id: fighterId,
    prefix: stats.prefix,
    stats,

    // ---- transform (authoritative at tick boundaries) ----
    x: index === 0 ? -SPAWN_X : SPAWN_X,
    y: GROUND_Y,
    z: 0,
    vx: 0,
    vy: 0,
    vz: 0,
    /** Previous-tick transform for render interpolation (ARCHITECTURE.md §2). */
    prevX: index === 0 ? -SPAWN_X : SPAWN_X,
    prevY: GROUND_Y,
    prevZ: 0,
    /** +1 faces +X, -1 faces -X. */
    facing: index === 0 ? 1 : -1,
    prevFacing: index === 0 ? 1 : -1,
    /**
     * Visual yaw in radians, tracked separately so facing flips animate rather than snap.
     *
     * Convention: fighter local +X is forward (ARCHITECTURE.md §15), so a fighter facing world +X
     * has yaw 0 and one facing world -X has yaw PI. Using +/-PI/2 here would point local +X along
     * world -Z, which renders both fighters with their backs to the camera.
     */
    yaw: index === 0 ? 0 : Math.PI,
    prevYaw: index === 0 ? 0 : Math.PI,

    // ---- state machine ----
    state: S.ROUND_INTRO,
    prevState: S.ROUND_INTRO,
    /** Ticks spent in the current state. */
    stateTick: 0,
    stateEnterTick: 0,
    /** Fighter-owned presentation clock. Advances only on unfrozen simulation ticks. */
    poseTick: 0,

    // ---- active move ----
    moveId: '',
    /** @type {object|null} resolved move definition; never serialised. */
    move: null,
    /** Ticks since the move began, including startup. */
    moveTick: 0,
    /** True once this move has connected (hit, blocked, or thrown) — prevents double hits. */
    moveHasHit: false,
    /** Per-hitbox hit flags so a multi-hitbox move cannot hit twice with the same box. */
    hitboxUsed: new Uint8Array(MAX_HITBOXES),
    /** Move buffered during the cancel window, applied when the window allows. */
    bufferedMoveId: '',
    bufferedAtTick: 0,
    /** Ticks the current string has been intentionally delayed (for delayable strings). */
    stringDelayTicks: 0,
    /** Id of the move that started the current string, for string-tree tracking. */
    stringRootId: '',

    // ---- stun and freeze ----
    hitstunTicks: 0,
    blockstunTicks: 0,
    /** Hitstop: the fighter is visually frozen but the underlying state is preserved. */
    frozenTicks: 0,
    /** Recovery lock used by dashes, sidesteps, landings and non-attack actions. */
    actionLockTicks: 0,

    // ---- health and resources ----
    maxHealth: stats.maxHealth,
    health: stats.maxHealth,
    guardMax: stats.guardMax,
    guardMeter: stats.guardMax,
    meterMax: stats.meterMax,
    meter: stats.meterStartOfRound,

    // ---- stance ----
    stance: '',
    stanceTicks: 0,

    // ---- combo tracking, as the ATTACKER ----
    comboHits: 0,
    comboDamage: 0,
    comboScale: 1,
    /** Starter value of the move that opened the combo. */
    comboStarter: 1,
    comboWallUsed: false,
    /** Tick of the most recent connected hit; used to detect combo end. */
    lastHitTick: -1000,

    // ---- juggle tracking, as the DEFENDER ----
    juggleCount: 0,
    juggleGravityMul: 1,
    /** True while the fighter can be hit by juggle-only moves. */
    juggleable: false,

    // ---- knockdown ----
    knockdownHard: false,
    faceUp: false,
    headTowardOpponent: false,
    groundTicks: 0,
    /** Chosen wake-up option, set when leaving KNOCKDOWN_GROUND. */
    wakeupOption: '',
    /** Invulnerable ticks remaining (wake-up, backdash, invuln windows). */
    invulnTicks: 0,
    /** Hit levels this fighter is currently immune to; empty means "all levels connect". */
    invulnLevels: [],

    // ---- throws ----
    /** Index of the fighter this one is grappling, or -1. */
    throwPartner: -1,
    throwMoveId: '',
    throwTick: 0,
    throwChainIndex: 0,
    /** Ticks remaining in which the victim may break the throw. */
    throwBreakTicks: 0,
    /** True once the victim has pressed a valid break during the window. */
    throwBreakArmed: false,

    // ---- armor ----
    armorHitsLeft: 0,
    armorDamageMul: 0,

    // ---- walls ----
    /** -1 = left wall, +1 = right wall, 0 = not against a wall. */
    wallSide: 0,
    wallStunTicks: 0,
    /** Set when a wall splat has already been used this combo (prevents infinite wall loops). */
    wallSplatUsed: false,

    // ---- parry ----
    parryTicks: 0,
    parrySucceeded: false,

    // ---- per-tick derived flags (recomputed by refreshDerived) ----
    /**
     * Coarse body posture, consumed by src/physics to pick a hurtbox profile and by
     * src/animation to pick a pose family. A plain string so physics never needs to know
     * the combat state vocabulary — see ARCHITECTURE.md §4 dependency rules.
     * One of: 'stand' | 'crouch' | 'air' | 'down' | 'slump' | 'splat'
     */
    posture: 'stand',
    airborne: false,
    grounded: true,
    crouching: false,
    guarding: false,
    attacking: false,
    inHitstun: false,
    down: false,
    throwable: true,
    counterVulnerable: false,
    punishable: false,
    locked: false,
    actionable: false,

    // ---- round bookkeeping ----
    roundsWon: 0,
    perfectRound: true,
    defeated: false,
    /** Damage taken this round, for telemetry and balance reports. */
    damageTakenRound: 0,
    damageDealtRound: 0,

    // ---- training / AI observation ----
    /** Ring buffer of recent completed actions: {tick, moveId, outcome}. */
    actionLog: new Array(ACTION_LOG_SIZE),
    actionLogHead: 0,

    // ---- input snapshot reference, refreshed each tick by the combat system ----
    /** @type {object|null} */
    inputs: null,

    /** Diagnostic: total ticks the fighter has been simulated. */
    aliveTicks: 0,
  };

  for (let i = 0; i < ACTION_LOG_SIZE; i++) {
    f.actionLog[i] = { tick: -1, moveId: '', outcome: '', state: '' };
  }
  refreshDerived(f);
  return f;
}

/**
 * Recomputes the cached derived flags from the current state. Called once per tick per
 * fighter, after any state change. Keeping these as cached booleans (rather than getters)
 * avoids repeated Set lookups in the hit-resolution inner loop.
 */
export function refreshDerived(f) {
  const s = f.state;
  f.airborne = isAirborneState(s);
  f.grounded = !f.airborne;
  f.crouching = isCrouchingState(s);
  f.guarding = isGuardingState(s);
  f.attacking = isAttackingState(s);
  f.inHitstun = isHitstunState(s);
  f.down = isDownState(s);
  f.throwable = isThrowableState(s) && f.invulnTicks <= 0;
  f.counterVulnerable = isCounterHitState(s);
  f.punishable = isPunishableState(s);
  f.locked = isLockedState(s) || f.hitstunTicks > 0 || f.blockstunTicks > 0;
  f.actionable = isActionableState(s) && f.actionLockTicks <= 0 && f.hitstunTicks <= 0
    && f.blockstunTicks <= 0 && f.frozenTicks <= 0;
  f.juggleable = isJuggleableState(s);
  f.posture = postureFor(s);
  return f;
}

/**
 * The visual yaw for a facing value. Local +X is forward, so facing +X is yaw 0.
 * Single source of truth — every site that sets yaw must use this.
 */
export function yawForFacing(facing) {
  return facing > 0 ? 0 : Math.PI;
}

/** Maps a combat state to the coarse posture that physics and animation consume. */
export function postureFor(s) {
  if (s === S.KNOCKDOWN_GROUND) return 'down';
  if (s === S.WALL_SLUMP) return 'slump';
  if (s === S.WALL_SPLAT) return 'splat';
  if (isCrouchingState(s)) return 'crouch';
  if (isAirborneState(s)) return 'air';
  return 'stand';
}

/** Copies the current transform into the previous-transform slots. Call at the top of a step. */
export function snapshotTransform(f) {
  f.prevX = f.x;
  f.prevY = f.y;
  f.prevZ = f.z;
  f.prevFacing = f.facing;
  f.prevYaw = f.yaw;
}

/** Height of the fighter's hurtbox top, accounting for crouch and air states. */
export function hurtboxHeight(f) {
  if (f.crouching) return f.stats.crouchHeight;
  if (f.down) return 0.42;
  if (f.state === S.WALL_SLUMP) return 0.9;
  if (f.airborne) return f.stats.airHeight;
  return f.stats.pushboxHeight;
}

/** Records a completed action for the habit tracker and training-mode input history. */
export function logAction(f, tick, moveId, outcome) {
  const slot = f.actionLog[f.actionLogHead];
  slot.tick = tick;
  slot.moveId = moveId;
  slot.outcome = outcome;
  slot.state = f.state;
  f.actionLogHead = (f.actionLogHead + 1) % ACTION_LOG_SIZE;
}

/** Oldest-first list of logged actions. Allocates — dev/AI-telemetry only. */
export function actionHistory(f) {
  const out = [];
  for (let i = 0; i < ACTION_LOG_SIZE; i++) {
    const slot = f.actionLog[(f.actionLogHead + i) % ACTION_LOG_SIZE];
    if (slot.tick >= 0) out.push(slot);
  }
  return out;
}

/**
 * Resets a fighter for a new round. Preserves `roundsWon` and identity; clears everything
 * else so a stale combo counter or armor flag can never leak across a round boundary.
 */
export function resetForRound(f, roundNumber) {
  const stats = f.stats;
  f.x = f.index === 0 ? -SPAWN_X : SPAWN_X;
  f.y = GROUND_Y;
  f.z = 0;
  f.vx = 0;
  f.vy = 0;
  f.vz = 0;
  f.prevX = f.x;
  f.prevY = f.y;
  f.prevZ = f.z;
  f.facing = f.index === 0 ? 1 : -1;
  f.prevFacing = f.facing;
  f.yaw = yawForFacing(f.facing);
  f.prevYaw = f.yaw;

  f.state = S.ROUND_INTRO;
  f.prevState = S.ROUND_INTRO;
  f.stateTick = 0;
  f.stateEnterTick = 0;
  f.poseTick = 0;

  f.moveId = '';
  f.move = null;
  f.moveTick = 0;
  f.moveHasHit = false;
  f.hitboxUsed.fill(0);
  f.bufferedMoveId = '';
  f.bufferedAtTick = 0;
  f.stringDelayTicks = 0;
  f.stringRootId = '';

  f.hitstunTicks = 0;
  f.blockstunTicks = 0;
  f.frozenTicks = 0;
  f.actionLockTicks = 0;

  f.health = stats.maxHealth;
  f.guardMeter = stats.guardMax;
  f.meter = stats.meterStartOfRound;

  f.stance = '';
  f.stanceTicks = 0;

  resetCombo(f);
  f.juggleCount = 0;
  f.juggleGravityMul = 1;

  f.knockdownHard = false;
  f.faceUp = false;
  f.headTowardOpponent = false;
  f.groundTicks = 0;
  f.wakeupOption = '';
  f.invulnTicks = 0;
  f.invulnLevels.length = 0;

  f.throwPartner = -1;
  f.throwMoveId = '';
  f.throwTick = 0;
  f.throwChainIndex = 0;
  f.throwBreakTicks = 0;
  f.throwBreakArmed = false;

  f.armorHitsLeft = 0;
  f.armorDamageMul = 0;

  f.wallSide = 0;
  f.wallStunTicks = 0;
  f.wallSplatUsed = false;

  f.parryTicks = 0;
  f.parrySucceeded = false;

  f.perfectRound = true;
  f.defeated = false;
  f.damageTakenRound = 0;
  f.damageDealtRound = 0;
  f.lastHitTick = -1000;
  f.aliveTicks = 0;

  for (let i = 0; i < ACTION_LOG_SIZE; i++) {
    const slot = f.actionLog[i];
    slot.tick = -1;
    slot.moveId = '';
    slot.outcome = '';
    slot.state = '';
  }
  f.actionLogHead = 0;
  void roundNumber;
  refreshDerived(f);
  return f;
}

export function resetCombo(f) {
  f.comboHits = 0;
  f.comboDamage = 0;
  f.comboScale = 1;
  f.comboStarter = 1;
  f.comboWallUsed = false;
}

/** Full match reset, including rounds won. */
export function resetForMatch(f) {
  f.roundsWon = 0;
  resetForRound(f, 1);
  return f;
}

/**
 * Converts f60 frames to ticks. Duplicated from core/math for call-site locality in the
 * hottest file in the simulation.
 */
export function f60ToTicks(frames) {
  return Math.round(frames * F60_TO_TICKS);
}
